export default class Toggle {
  onlyOpen = 'data-open';

  onlyClose = 'data-close';

  openClass = 'opened';

  activeClass = 'active';

  timer: number = null;

  constructor(toggleElements: NodeListOf<Element>) {
    /**
     * ESS-2667 - some scenarios doesn't track accordion buttons
     * so we need to filter them out to make sure that we are
     * adding event listener only to accordion buttons
     */
    const accordionButtons = Array.from(toggleElements).filter((element) => element.classList.contains('accordion-button'));
    const nonAccordionButtons = Array.from(toggleElements).filter((element) => !element.classList.contains('accordion-button'));

    accordionButtons.forEach((element) => {
      element.addEventListener('click', this.handler.bind(this));
      const triggerWrapper = this.getTriggerWrapper(element as HTMLElement);
      const contentId = triggerWrapper.dataset.toggleid.split(' ');
      const content = this.getContentId(contentId);
      if (content.classList.contains('accordion-content-wrap')) {
        this.setAccesabilityForAccordion(content);
      }
    });

    nonAccordionButtons.forEach((element) => {
      element.addEventListener('click', this.handler.bind(this));
    });

    this.hashHandler();
  }

  getContentId = (contentId: string[]) => document.getElementById(contentId[0]) as HTMLDivElement;

  getTriggerWrapper = (trigger: HTMLElement) => trigger.closest('.js-toggle-wrap') as HTMLDivElement;

  handler(ev: Event) {
    const trigger = (ev.currentTarget) as HTMLElement;
    const isBurgerMenu = trigger.classList.contains('js-burger-menu');
    const triggerWrapper = this.getTriggerWrapper(trigger);
    const contentId = triggerWrapper.dataset.toggleid.split(' ');
    const content = this.getContentId(contentId);

    if (isBurgerMenu && triggerWrapper.classList.contains('active')) {
      const megaMenuList = trigger.dataset.idList.split(' ');
      if (megaMenuList) {
        megaMenuList.forEach((idName) => {
          const element = document.getElementById(idName);
          element.classList.remove('opened');
        });
        triggerWrapper.classList.remove('active');
        return;
      }
      return;
    }

    if (content) {
      const isCloseOnly = trigger.getAttribute(this.onlyClose)
        || trigger.hasAttribute(this.onlyClose);
      const isOpenOnly = trigger.getAttribute(this.onlyOpen)
        || trigger.hasAttribute(this.onlyOpen);

      // Only to open element by ID provided from trigger
      if (isOpenOnly) {
        this.onlyOpenElement(content, triggerWrapper);
        return;
      }

      // Only to close opened elements by ID provided from trigger
      if (isCloseOnly) {
        /*
            remove all active class from triggers
            example laguage region selector
        */
        if (isCloseOnly === 'all') {
          const toggleWrap = document.querySelectorAll(`.js-toggle-wrap[data-toggleId="${contentId}"]`);
          // Accesaibility focus to back element
          const backFocusElement = document.querySelectorAll(`.js-toggle[data-mega-menu="${contentId}"]`);
          if (backFocusElement.length) {
            (backFocusElement[0] as HTMLElement).focus();
          }

          Array.from(toggleWrap).forEach((element) => {
            element.classList.remove(this.activeClass);
          });
        }

        /**
         * if there is more content close them all
         * example navigation and mega menu relation on mobile devices
         */
        if (contentId.length > 1) {
          this.closeAllOpenedById(contentId);
          return;
        }

        content.classList.remove(this.openClass);
        triggerWrapper.classList.remove(this.activeClass);

        return;
      }
      content.classList.toggle(this.openClass);
      triggerWrapper.classList.toggle(this.activeClass);
      this.accesaibilityHandler(trigger, content);
      this.accordionAccesabilityHandler(content);
    }
  }

  /**
   * Close all opened elements by provided id from close trigger
   * @param contentId - array of opened elements
   * @returns
   */
  closeAllOpenedById(contentId: string[]) {
    return contentId.forEach((id) => {
      const toggleWrap = document.querySelectorAll(`.js-toggle-wrap[data-toggleId="${id}"]`);
      Array.from(toggleWrap).forEach((element) => element.classList.remove(this.activeClass));
      const targetElement = document.getElementById(id);
      targetElement.classList.remove(this.openClass);
    });
  }

  /**
   * Open content by provided id from trigger
   * @param content html element to open
   * @param triggerWrapper trigger
   */
  onlyOpenElement(content: HTMLDivElement, triggerWrapper: HTMLDivElement) {
    content.classList.add(this.openClass);
    triggerWrapper.classList.add(this.activeClass);
  }

  /**
   * Automaticly opens accordion or dropdown if there is # in url
   * Notes: #{name} and html element id="toggle-{name}" needs to be same
   */
  hashHandler() {
    const hash = window.location.hash.slice(1);
    window.clearTimeout(this.timer);
    if (hash) {
      const hashElem = document.querySelector(`#toggle-${hash}`);
      if (hashElem) {
        const jsToggleWrap = hashElem.parentElement;
        if (jsToggleWrap.classList.contains('js-toggle-wrap')) {
          this.timer = window.setTimeout(() => {
            if (jsToggleWrap.classList.contains('js-toggle-wrap')) {
              const navbar = document.querySelector('.js-fixed.navbar') as HTMLElement | null;
              const yAxes = jsToggleWrap.getBoundingClientRect().top
                + window.pageYOffset - navbar.offsetHeight;

              window.scrollTo({ top: yAxes, behavior: 'smooth' });
              const nested = jsToggleWrap.parentElement.closest('.js-toggle-wrap');
              if (nested) {
                nested.classList.add('active');
              }
              jsToggleWrap.classList.add('active');
            }
          }, 1000);
        }
      }
    }
  }

  /**
   * Set aria-expanded and aria-hidden attributes
   * @param trigger elemetn that is clicked
   * @param content target element to open or close
   */
  accesaibilityHandler(trigger: HTMLElement, content: HTMLDivElement) {
    const isExpanded = trigger.getAttribute('aria-expanded') === 'true';
    trigger.setAttribute('aria-expanded', String(!isExpanded));

    const isHidden = content.getAttribute('aria-hidden') === 'true';
    content.setAttribute('aria-hidden', String(!isHidden));
  }

  /**
   * Set tabindex to -1 if element is hidden
   * @param content target element to open or close
   */
  accordionAccesabilityHandler(content: HTMLElement) {
    const isAccordionContent = content.classList.contains('accordion-content-wrap');
    if (isAccordionContent) {
      const isOpened = content.classList.contains(this.openClass);
      const accesabilityElements = content.querySelectorAll('a, button');
      if (isOpened) {
        Array.from(accesabilityElements).forEach((element) => {
          element.setAttribute('tabindex', '0');
        });
      }

      if (!isOpened) {
        if (content.classList.contains('accordion-content-wrap')) {
          content.setAttribute('aria-hidden', 'true');
        }
        this.preventTabForHiddenElements(accesabilityElements);
      }
    }
  }

  setAccesabilityForAccordion(content: HTMLElement) {
    const accesabilityElements = content.querySelectorAll('a, button');
    this.preventTabForHiddenElements(accesabilityElements);
  }

  preventTabForHiddenElements(accesabilityElements: NodeListOf<Element>) {
    Array.from(accesabilityElements).forEach((element) => {
      element.setAttribute('tabindex', '-1');
    });
  }
}
